import { Search, Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { OrderColumnVisibilityToggle } from './OrderColumnVisibilityToggle';
import type { Organisation, QuoteStatus, OrderColumnKey } from '../types';
import type { SalesManager } from '@/features/accounts/api';
import { ORDER_STATUSES } from '../types';

interface OrdersFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  customerId: string;
  onCustomerChange: (value: string) => void;
  salesManagerId: string;
  onSalesManagerChange: (value: string) => void;
  selectedStatuses: QuoteStatus[];
  onStatusChange: (statuses: QuoteStatus[]) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  customers: Organisation[];
  isLoadingCustomers: boolean;
  salesManagers: SalesManager[];
  isLoadingSalesManagers: boolean;
  onExport: () => void;
  columnVisibility: Record<OrderColumnKey, boolean>;
  onColumnToggle: (key: OrderColumnKey) => void;
}

export function OrdersFilters({
  search,
  onSearchChange,
  customerId,
  onCustomerChange,
  salesManagerId,
  onSalesManagerChange,
  selectedStatuses,
  onStatusChange,
  pageSize,
  onPageSizeChange,
  customers,
  isLoadingCustomers,
  salesManagers,
  isLoadingSalesManagers,
  onExport,
  columnVisibility,
  onColumnToggle,
}: OrdersFiltersProps) {
  const { t } = useTranslation('orders');

  const handleStatusToggle = (status: QuoteStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusChange([...selectedStatuses, status]);
    }
  };

  const handleClearStatuses = () => {
    onStatusChange([]);
  };

  const statusButtonLabel =
    selectedStatuses.length === 0
      ? t('filters.allStatuses')
      : selectedStatuses.length === ORDER_STATUSES.length
        ? t('filters.allStatuses')
        : t('filters.statusCount', { count: selectedStatuses.length });

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t('filters.searchPlaceholder')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-64 pl-8"
        />
      </div>

      {/* Customer Filter */}
      <Select value={customerId} onValueChange={onCustomerChange}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={isLoadingCustomers ? t('filters.loading') : t('filters.allCustomers')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allCustomers')}</SelectItem>
          {customers.map((customer) => (
            <SelectItem key={customer._id} value={customer._id}>
              {customer.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Account Manager Filter */}
      <Select value={salesManagerId} onValueChange={onSalesManagerChange}>
        <SelectTrigger className="w-48">
          <SelectValue placeholder={isLoadingSalesManagers ? t('filters.loading') : t('filters.allSalesManagers')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allSalesManagers')}</SelectItem>
          {salesManagers.map((manager) => (
            <SelectItem key={manager.id} value={manager.id}>
              {manager.name}{manager.isFormer ? ' (Former)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Status Filter (Multi-select) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-40 justify-between">
            {statusButtonLabel}
            {selectedStatuses.length > 0 && selectedStatuses.length < ORDER_STATUSES.length && (
              <X
                className="h-3 w-3 ml-1 hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearStatuses();
                }}
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {ORDER_STATUSES.map((status) => (
            <DropdownMenuCheckboxItem
              key={status}
              checked={selectedStatuses.includes(status)}
              onCheckedChange={() => handleStatusToggle(status)}
            >
              {t(`status.${status}`)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Page Size */}
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="25">25</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>

      {/* Column Visibility */}
      <OrderColumnVisibilityToggle
        visibleColumns={columnVisibility}
        onToggle={onColumnToggle}
      />

      {/* Export */}
      <Button
        variant="outline"
        size="icon"
        onClick={onExport}
        title={t('filters.exportCsv')}
        aria-label={t('filters.exportCsv')}
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}
